import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Self-service password reset for patients who simply forgot their password.
 * If the account is locked (5 failed logins), the API responds with 423 and we
 * redirect them to the admin-approved recovery flow.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWith } = useAuth();
  const [email, setEmail] = useState(params.get("email") || "");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setOtpSent(true);
      toast.success("If an account exists, an OTP has been sent to your email.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        email: email.trim(), otp: otp.trim(), new_password: newPassword,
      });
      loginWith(data.user);
      toast.success("Password reset — you're logged in!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err?.response?.status === 423) {
        toast.error("Account locked. Redirecting to account recovery…");
        setTimeout(() => navigate(`/recover-account?email=${encodeURIComponent(email)}`), 1200);
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 md:py-24" data-testid="forgot-password-page">
      <div className="bg-white rounded-3xl border border-[#EDF1EF] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        <span className="w-12 h-12 rounded-2xl bg-[#D9E4E1] flex items-center justify-center mb-5">
          <KeyRound className="w-6 h-6 text-[#4A7065]" />
        </span>
        <h1 className="font-heading text-3xl text-slate-900">Forgot password</h1>
        <p className="text-sm text-slate-500 mt-1">
          Enter your registered email — we'll send a 6-digit OTP so you can set a new password.
        </p>

        {!otpSent ? (
          <form onSubmit={requestOtp} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="forgot-email">Registered email</Label>
              <Input
                id="forgot-email"
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <Button
              data-testid="forgot-send-otp-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6"
            >
              {loading ? "Sending…" : "Send OTP"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="mt-7 space-y-4">
            <p className="text-xs text-slate-500">
              We've sent a 6-digit OTP to <b>{email}</b>. It expires in 10 minutes.
            </p>
            <div>
              <Label htmlFor="forgot-otp">OTP from your email</Label>
              <Input
                id="forgot-otp"
                data-testid="forgot-otp-input"
                inputMode="numeric"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit code"
                className="mt-1.5 rounded-xl tracking-[0.5em] text-center"
              />
            </div>
            <div>
              <Label htmlFor="forgot-newpass">New password</Label>
              <Input
                id="forgot-newpass"
                data-testid="forgot-newpass-input"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <Button
              data-testid="forgot-reset-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6"
            >
              {loading ? "Resetting…" : "Reset password and log in"}
            </Button>
            <button
              type="button"
              data-testid="forgot-resend-btn"
              onClick={() => { setOtp(""); setOtpSent(false); }}
              className="text-xs text-slate-500 hover:text-[#2C557E] block mx-auto"
            >
              Didn't get it? Resend OTP
            </button>
          </form>
        )}

        <p className="text-sm text-slate-500 text-center mt-6">
          Remembered it?{" "}
          <Link to="/login" data-testid="forgot-back-login" className="text-[#2C557E] font-medium hover:underline">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
